import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Calendar, Search, Trash2, Edit, Plus, Download, Upload, Loader2, ArrowLeft, 
  ShieldX, MapPin, Users, Building2, TrendingUp, FileSpreadsheet, X, Check,
  Globe, Clock, Link
} from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// URL Import Dialog Component
function ImportFromURLDialog({ open, onClose, onImport, importing, title }) {
  const [url, setUrl] = useState("");
  
  const handleImport = () => {
    if (!url.trim()) {
      toast.error("Please enter a URL");
      return;
    }
    onImport(url.trim());
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-blue-600" />
            Import {title} from Website
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <p className="text-sm text-slate-600">
            Paste the event/conference page URL and we'll automatically extract dates, location, speakers, sponsors, pharma/investor presence, and more.
          </p>
          <div className="space-y-2">
            <Label>Website URL</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="https://biotech-conference.com"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  className="pl-9"
                  onKeyDown={(e) => e.key === 'Enter' && handleImport()}
                />
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={importing}>
            Cancel
          </Button>
          <Button onClick={handleImport} disabled={importing || !url.trim()}>
            {importing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Extracting...
              </>
            ) : (
              <>
                <Globe className="w-4 h-4 mr-2" />
                Extract Data
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const EVENT_TYPES = [
  { value: "conference", label: "Conference" },
  { value: "summit", label: "Summit" },
  { value: "networking", label: "Networking" },
  { value: "pitch", label: "Pitch Event" },
  { value: "workshop", label: "Workshop" }
];

const THEME_OPTIONS = [
  "therapeutics", "oncology", "rare diseases", "gene therapy", "cell therapy",
  "immunotherapy", "digital health", "partnerships", "licensing", "investment",
  "drug discovery", "clinical trials", "biotech", "pharma", "M&A"
];

const AUDIENCE_OPTIONS = [
  "founders", "investors", "pharma", "researchers", "executives", "clinicians", "policy makers"
];

export default function AdminEvents() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Events state
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  
  // Dialogs
  const [eventDialog, setEventDialog] = useState({ open: false, event: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, event: null });
  const [uploadDialog, setUploadDialog] = useState({ open: false });
  const [importing, setImporting] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // Form state
  const [eventForm, setEventForm] = useState({
    name: "", description: "", start_date: "", end_date: "",
    location: "", city: "", country: "", event_type: "conference",
    themes: [], target_audience: [], key_speakers: [],
    pharma_presence: [], investor_presence: [],
    networking_score: 50, bd_opportunities: "",
    estimated_attendees: "", registration_fee: "", website: ""
  });
  
  const [editTab, setEditTab] = useState("basic");
  
  // Bulk upload state
  const [uploadData, setUploadData] = useState([]);
  const [uploadResult, setUploadResult] = useState(null);

  useEffect(() => {
    checkAccess();
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchEvents();
    }
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin) {
      const t = setTimeout(fetchEvents, 300);
      return () => clearTimeout(t);
    }
  }, [search, isAdmin]);

  const checkAccess = async () => {
    try {
      const userRes = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: "include" });
      if (!userRes.ok) { navigate("/auth"); return; }
      setUser(await userRes.json());

      const adminRes = await fetch(`${BACKEND_URL}/api/admin/check`, { credentials: "include" });
      if (adminRes.ok) {
        const data = await adminRes.json();
        setIsAdmin(data.is_admin);
      } else {
        setIsAdmin(false);
      }
    } catch (error) {
      navigate("/auth");
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/events`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        let filtered = data.events;
        if (search) {
          const s = search.toLowerCase();
          filtered = filtered.filter(e => 
            e.name?.toLowerCase().includes(s) ||
            e.location?.toLowerCase().includes(s) ||
            e.city?.toLowerCase().includes(s)
          );
        }
        setEvents(filtered);
        setTotal(data.total);
      }
    } catch (error) {
      console.error("Failed to fetch events:", error);
    }
  };

  const handleImportFromURL = async (url) => {
    if (!url || !url.trim()) {
      toast.error("Please enter a URL");
      return;
    }
    setImporting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/events/import-url`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url })
      });
      
      if (response.ok) {
        const data = await response.json();
        const extracted = data.extracted_data;
        
        // Convert speakers array if needed
        const newSpeakers = (extracted.speakers || []).map(s => {
          if (typeof s === 'string') return { name: s, title: "", company: "" };
          return { name: s.name || "", title: s.title || "", company: s.company || "" };
        });
        
        // Check if we're editing an existing event (merge mode) or creating new (replace mode)
        const isEditing = eventDialog.event !== null;
        
        if (isEditing) {
          // MERGE MODE: Fill empty fields, append to arrays
          const mergeArrays = (existing, newItems) => {
            const existingSet = new Set((existing || []).map(i => typeof i === 'string' ? i.toLowerCase() : JSON.stringify(i)));
            const uniqueNew = (newItems || []).filter(i => !existingSet.has(typeof i === 'string' ? i.toLowerCase() : JSON.stringify(i)));
            return [...(existing || []), ...uniqueNew];
          };
          
          const mergeSpeakers = (existing, newItems) => {
            const existingNames = new Set((existing || []).map(s => s.name?.toLowerCase()));
            const uniqueNew = (newItems || []).filter(s => s.name && !existingNames.has(s.name.toLowerCase()));
            return [...(existing || []).filter(s => s.name), ...uniqueNew];
          };
          
          setEventForm(prev => ({
            name: prev.name || extracted.name || "",
            description: prev.description || extracted.description || "",
            start_date: prev.start_date || extracted.start_date || "",
            end_date: prev.end_date || extracted.end_date || "",
            location: prev.location || extracted.venue || extracted.location || "",
            city: prev.city || extracted.location?.split(",")[0]?.trim() || "",
            country: prev.country || extracted.location?.split(",").slice(-1)[0]?.trim() || "",
            event_type: prev.event_type || extracted.event_type?.toLowerCase() || "conference",
            themes: mergeArrays(prev.themes, extracted.themes),
            target_audience: mergeArrays(prev.target_audience, extracted.target_audience),
            key_speakers: mergeSpeakers(prev.key_speakers, newSpeakers).length > 0 
              ? mergeSpeakers(prev.key_speakers, newSpeakers) 
              : [{ name: "", title: "", company: "" }],
            pharma_presence: mergeArrays(prev.pharma_presence, extracted.pharma_presence),
            investor_presence: mergeArrays(prev.investor_presence, extracted.investor_presence),
            networking_score: prev.networking_score || 50,
            bd_opportunities: prev.bd_opportunities || extracted.networking_opportunities || "",
            estimated_attendees: prev.estimated_attendees || extracted.expected_attendees?.toString() || "",
            registration_fee: prev.registration_fee || extracted.registration_fee || "",
            website: prev.website || extracted.website || url
          }));
          
          const addedSpeakers = mergeSpeakers(eventForm.key_speakers, newSpeakers).length - (eventForm.key_speakers?.filter(s => s.name).length || 0);
          toast.success(`Data merged! Empty fields filled${addedSpeakers > 0 ? `, ${addedSpeakers} new speakers added` : ""}.`);
        } else {
          // NEW MODE: Replace all fields
          setEventForm({
            name: extracted.name || "",
            description: extracted.description || "",
            start_date: extracted.start_date || "",
            end_date: extracted.end_date || "",
            location: extracted.venue || extracted.location || "",
            city: extracted.location?.split(",")[0]?.trim() || "",
            country: extracted.location?.split(",").slice(-1)[0]?.trim() || "",
            event_type: extracted.event_type?.toLowerCase() || "conference",
            themes: extracted.themes || [],
            target_audience: extracted.target_audience || [],
            key_speakers: newSpeakers.length > 0 ? newSpeakers : [{ name: "", title: "", company: "" }],
            pharma_presence: extracted.pharma_presence || [],
            investor_presence: extracted.investor_presence || [],
            networking_score: 50,
            bd_opportunities: extracted.networking_opportunities || "",
            estimated_attendees: extracted.expected_attendees?.toString() || "",
            registration_fee: extracted.registration_fee || "",
            website: extracted.website || url
          });
          toast.success("Data extracted! Review the form below.");
        }
        
        setImportUrl("");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to extract data");
      }
    } catch (error) {
      toast.error("Failed to extract data from URL");
    } finally {
      setImporting(false);
    }
  };

  const openEventDialog = (event = null) => {
    if (event) {
      setEventForm({
        name: event.name || "",
        description: event.description || "",
        start_date: event.start_date?.split("T")[0] || "",
        end_date: event.end_date?.split("T")[0] || "",
        location: event.location || "",
        city: event.city || "",
        country: event.country || "",
        event_type: event.event_type || "conference",
        themes: event.themes || [],
        target_audience: event.target_audience || [],
        key_speakers: event.key_speakers || [],
        pharma_presence: event.pharma_presence || [],
        investor_presence: event.investor_presence || [],
        networking_score: event.networking_score || 50,
        bd_opportunities: event.bd_opportunities || "",
        estimated_attendees: event.estimated_attendees || "",
        registration_fee: event.registration_fee || "",
        website: event.website || ""
      });
    } else {
      setEventForm({
        name: "", description: "", start_date: "", end_date: "",
        location: "", city: "", country: "", event_type: "conference",
        themes: [], target_audience: [], key_speakers: [],
        pharma_presence: [], investor_presence: [],
        networking_score: 50, bd_opportunities: "",
        estimated_attendees: "", registration_fee: "", website: ""
      });
    }
    setEditTab("basic");
    setEventDialog({ open: true, event });
  };

  const saveEvent = async () => {
    if (!eventForm.name || !eventForm.start_date) {
      toast.error("Name and start date are required");
      return;
    }
    
    setSaving(true);
    try {
      const url = eventDialog.event 
        ? `${BACKEND_URL}/api/admin/events/${eventDialog.event.event_id}`
        : `${BACKEND_URL}/api/admin/events`;
      
      const response = await fetch(url, {
        method: eventDialog.event ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(eventForm)
      });
      
      if (response.ok) {
        toast.success(eventDialog.event ? "Event updated" : "Event created");
        setEventDialog({ open: false, event: null });
        fetchEvents();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to save event");
      }
    } catch (error) {
      toast.error("Failed to save event");
    } finally {
      setSaving(false);
    }
  };

  const deleteEvent = async () => {
    if (!deleteDialog.event) return;
    setSaving(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/events/${deleteDialog.event.event_id}`,
        { method: "DELETE", credentials: "include" }
      );
      if (response.ok) {
        toast.success("Event deleted");
        setDeleteDialog({ open: false, event: null });
        fetchEvents();
      } else {
        toast.error("Failed to delete event");
      }
    } catch (error) {
      toast.error("Failed to delete event");
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/events/export`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        
        // Convert to CSV
        const headers = Object.keys(data.events[0] || {});
        const csvRows = [headers.join(",")];
        
        for (const event of data.events) {
          const row = headers.map(h => {
            let val = event[h] || "";
            // Escape quotes and wrap in quotes if contains comma
            if (typeof val === "string" && (val.includes(",") || val.includes('"') || val.includes("\n"))) {
              val = `"${val.replace(/"/g, '""')}"`;
            }
            return val;
          });
          csvRows.push(row.join(","));
        }
        
        const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `events_export_${new Date().toISOString().split("T")[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        
        toast.success("Events exported successfully");
      }
    } catch (error) {
      toast.error("Failed to export events");
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target?.result;
        const rows = text.split("\n").filter(row => row.trim());
        
        if (rows.length < 2) {
          toast.error("File must have header row and at least one data row");
          return;
        }
        
        // Parse CSV
        const parseCSVRow = (row) => {
          const result = [];
          let current = "";
          let inQuotes = false;
          
          for (let i = 0; i < row.length; i++) {
            const char = row[i];
            if (char === '"') {
              if (inQuotes && row[i + 1] === '"') {
                current += '"';
                i++;
              } else {
                inQuotes = !inQuotes;
              }
            } else if (char === "," && !inQuotes) {
              result.push(current.trim());
              current = "";
            } else {
              current += char;
            }
          }
          result.push(current.trim());
          return result;
        };
        
        const headers = parseCSVRow(rows[0]).map(h => h.toLowerCase().replace(/\s+/g, "_"));
        const events = [];
        
        for (let i = 1; i < rows.length; i++) {
          const values = parseCSVRow(rows[i]);
          if (values.length < 2) continue;
          
          const event = {};
          headers.forEach((header, idx) => {
            event[header] = values[idx] || "";
          });
          events.push(event);
        }
        
        setUploadData(events);
        setUploadResult(null);
        setUploadDialog({ open: true });
        
      } catch (error) {
        toast.error("Failed to parse file");
      }
    };
    
    reader.readAsText(file);
    e.target.value = "";
  };

  const handleBulkUpload = async () => {
    if (uploadData.length === 0) {
      toast.error("No data to upload");
      return;
    }
    
    setUploading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/events/bulk-upload`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ events: uploadData })
      });
      
      if (response.ok) {
        const result = await response.json();
        setUploadResult(result);
        fetchEvents();
        toast.success(`Uploaded: ${result.created} created, ${result.updated} updated`);
      } else {
        const data = await response.json();
        toast.error(data.detail || "Upload failed");
      }
    } catch (error) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  };

  // Tag input helpers
  const addTag = (field, value) => {
    if (value && !eventForm[field].includes(value)) {
      setEventForm({ ...eventForm, [field]: [...eventForm[field], value] });
    }
  };

  const removeTag = (field, value) => {
    setEventForm({ ...eventForm, [field]: eventForm[field].filter(t => t !== value) });
  };

  // Speaker helpers
  const addSpeaker = () => {
    setEventForm({
      ...eventForm,
      key_speakers: [...eventForm.key_speakers, { name: "", title: "", company: "" }]
    });
  };

  const updateSpeaker = (index, field, value) => {
    const updated = [...eventForm.key_speakers];
    updated[index] = { ...updated[index], [field]: value };
    setEventForm({ ...eventForm, key_speakers: updated });
  };

  const removeSpeaker = (index) => {
    setEventForm({
      ...eventForm,
      key_speakers: eventForm.key_speakers.filter((_, i) => i !== index)
    });
  };

  // Handle CSV upload for attendees (pharma or investors)
  const handleAttendeeCSV = (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target?.result || "";
        const lines = text.split(/[\n\r]+/).filter(line => line.trim());
        const newEntries = [];
        
        for (const line of lines) {
          // Split by comma and clean up each entry
          const entries = line.split(",").map(e => e.trim()).filter(e => e && e.toLowerCase() !== field.replace("_", " "));
          newEntries.push(...entries);
        }
        
        // Add new unique entries to existing list
        const existing = eventForm[field] || [];
        const combined = [...new Set([...existing, ...newEntries])];
        setEventForm({ ...eventForm, [field]: combined });
        
        toast.success(`Added ${newEntries.length} entries to ${field === "pharma_presence" ? "Pharma" : "Investors"}`);
      } catch (error) {
        toast.error("Failed to parse file");
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  // Handle CSV upload for speakers
  const handleSpeakerCSV = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target?.result || "";
        const lines = text.split(/[\n\r]+/).filter(line => line.trim());
        
        if (lines.length < 1) {
          toast.error("File is empty");
          return;
        }
        
        // Parse CSV - check if first line is header
        const firstLine = lines[0].toLowerCase();
        const hasHeader = firstLine.includes("name") || firstLine.includes("title") || firstLine.includes("company");
        const startIndex = hasHeader ? 1 : 0;
        
        const newSpeakers = [];
        for (let i = startIndex; i < lines.length; i++) {
          const parts = lines[i].split(",").map(p => p.trim().replace(/^["']|["']$/g, ""));
          if (parts[0]) {
            newSpeakers.push({
              name: parts[0] || "",
              title: parts[1] || "",
              company: parts[2] || ""
            });
          }
        }
        
        // Add to existing speakers
        setEventForm({ 
          ...eventForm, 
          key_speakers: [...eventForm.key_speakers, ...newSpeakers] 
        });
        
        toast.success(`Added ${newSpeakers.length} speakers`);
      } catch (error) {
        toast.error("Failed to parse speaker file");
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  if (isAdmin === null || loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  if (isAdmin === false) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar user={user} />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <Card className="max-w-md mx-auto border border-red-200">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <ShieldX className="w-8 h-8 text-red-500" />
              </div>
              <h2 className="text-xl font-semibold text-slate-900 mb-2">Access Denied</h2>
              <p className="text-slate-500 mb-6">You don't have permission to access this page.</p>
              <Button onClick={() => navigate("/dashboard")}>Go to Dashboard</Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar user={user} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/admin")} className="rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Manage Events</h1>
            <p className="text-slate-600 mt-1">Add, edit, and manage biotech events</p>
          </div>
        </div>

        {/* Actions Bar */}
        <Card className="border border-slate-200 mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
              <div className="relative w-full sm:w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search events..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="search-events"
                />
              </div>
              <div className="flex gap-3">
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                  <Upload className="w-4 h-4 mr-2" />
                  Import CSV
                </Button>
                <Button variant="outline" onClick={handleExport}>
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
                <Button onClick={() => openEventDialog()} className="bg-teal-600 hover:bg-teal-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Event
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Events Table */}
        <Card className="border border-slate-200">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Events ({events.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {events.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Calendar className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>No events found</p>
                <Button variant="link" onClick={() => openEventDialog()} className="mt-2">
                  Add your first event
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Event</TableHead>
                      <TableHead>Dates</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Attendees</TableHead>
                      <TableHead>Speakers</TableHead>
                      <TableHead className="w-24">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {events.map((event) => (
                      <TableRow key={event.event_id} className="hover:bg-slate-50">
                        <TableCell>
                          <div>
                            <p className="font-medium text-slate-900">{event.name}</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {event.themes?.slice(0, 3).map((theme, i) => (
                                <Badge key={i} variant="secondary" className="text-xs">{theme}</Badge>
                              ))}
                              {(event.themes?.length || 0) > 3 && (
                                <Badge variant="secondary" className="text-xs">+{event.themes.length - 3}</Badge>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <div>{formatDate(event.start_date)}</div>
                            {event.end_date && (
                              <div className="text-slate-500">to {formatDate(event.end_date)}</div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm">
                            <MapPin className="w-3 h-3 text-slate-400" />
                            {event.city || event.location || "—"}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {event.event_type || "conference"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1 text-xs">
                            {event.pharma_presence?.length > 0 && (
                              <span className="flex items-center gap-1">
                                <Building2 className="w-3 h-3" />
                                {event.pharma_presence.length} pharma
                              </span>
                            )}
                            {event.investor_presence?.length > 0 && (
                              <span className="flex items-center gap-1">
                                <TrendingUp className="w-3 h-3" />
                                {event.investor_presence.length} investors
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm">
                            <Users className="w-3 h-3 text-slate-400" />
                            {event.key_speakers?.length || 0}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEventDialog(event)}
                              className="text-slate-400 hover:text-teal-600"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setDeleteDialog({ open: true, event })}
                              className="text-slate-400 hover:text-red-500"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Event Edit/Create Dialog */}
      <Dialog open={eventDialog.open} onOpenChange={(open) => setEventDialog({ open, event: eventDialog.event })}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {eventDialog.event ? "Edit Event" : "Add New Event"}
            </DialogTitle>
          </DialogHeader>

          {/* URL Import Section - shown for both new and editing */}
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Globe className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-900">
                {eventDialog.event ? "Complete from Website" : "Import from Website"}
              </span>
            </div>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="https://conference-website.com"
                  value={importUrl}
                  onChange={(e) => setImportUrl(e.target.value)}
                  className="pl-9"
                  onKeyDown={(e) => e.key === 'Enter' && handleImportFromURL(importUrl)}
                />
              </div>
              <Button 
                onClick={() => handleImportFromURL(importUrl)} 
                disabled={importing || !importUrl.trim()}
                variant="outline"
                className="text-blue-600 border-blue-300 hover:bg-blue-100"
              >
                {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />}
                <span className="ml-2">{importing ? "Extracting..." : "Extract"}</span>
              </Button>
            </div>
            <p className="text-xs text-blue-700 mt-2">
              {eventDialog.event 
                ? "Fill empty fields and add new speakers, themes, and attendees (won't overwrite existing data)"
                : "Paste an event/conference URL to auto-fill dates, speakers, sponsors, and more"
              }
            </p>
          </div>

          <Tabs value={editTab} onValueChange={setEditTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="attendees">Attendees</TabsTrigger>
              <TabsTrigger value="speakers">Speakers</TabsTrigger>
              <TabsTrigger value="details">Details</TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>Event Name *</Label>
                  <Input
                    value={eventForm.name}
                    onChange={(e) => setEventForm({ ...eventForm, name: e.target.value })}
                    placeholder="BIO-Europe Spring 2026"
                  />
                </div>
                <div>
                  <Label>Start Date *</Label>
                  <Input
                    type="date"
                    value={eventForm.start_date}
                    onChange={(e) => setEventForm({ ...eventForm, start_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={eventForm.end_date}
                    onChange={(e) => setEventForm({ ...eventForm, end_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Location</Label>
                  <Input
                    value={eventForm.location}
                    onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                    placeholder="Barcelona, Spain"
                  />
                </div>
                <div>
                  <Label>City</Label>
                  <Input
                    value={eventForm.city}
                    onChange={(e) => setEventForm({ ...eventForm, city: e.target.value })}
                    placeholder="Barcelona"
                  />
                </div>
                <div>
                  <Label>Country</Label>
                  <Input
                    value={eventForm.country}
                    onChange={(e) => setEventForm({ ...eventForm, country: e.target.value })}
                    placeholder="Spain"
                  />
                </div>
                <div>
                  <Label>Event Type</Label>
                  <Select
                    value={eventForm.event_type}
                    onValueChange={(v) => setEventForm({ ...eventForm, event_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {EVENT_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={eventForm.description}
                  onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                  placeholder="Brief description of the event..."
                  rows={3}
                />
              </div>

              <div>
                <Label>Themes</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {eventForm.themes.map((theme, i) => (
                    <Badge key={i} variant="secondary" className="gap-1">
                      {theme}
                      <X className="w-3 h-3 cursor-pointer" onClick={() => removeTag("themes", theme)} />
                    </Badge>
                  ))}
                </div>
                <Select onValueChange={(v) => addTag("themes", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Add theme..." />
                  </SelectTrigger>
                  <SelectContent>
                    {THEME_OPTIONS.filter(t => !eventForm.themes.includes(t)).map(theme => (
                      <SelectItem key={theme} value={theme}>{theme}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Target Audience</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {eventForm.target_audience.map((aud, i) => (
                    <Badge key={i} variant="secondary" className="gap-1">
                      {aud}
                      <X className="w-3 h-3 cursor-pointer" onClick={() => removeTag("target_audience", aud)} />
                    </Badge>
                  ))}
                </div>
                <Select onValueChange={(v) => addTag("target_audience", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Add audience..." />
                  </SelectTrigger>
                  <SelectContent>
                    {AUDIENCE_OPTIONS.filter(a => !eventForm.target_audience.includes(a)).map(aud => (
                      <SelectItem key={aud} value={aud}>{aud}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </TabsContent>

            <TabsContent value="attendees" className="space-y-4">
              {/* CSV Upload for Attendees */}
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <Label className="text-sm font-medium">Bulk Import Attendees</Label>
                    <p className="text-xs text-slate-500">Upload CSV with company names (one per line or comma-separated)</p>
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="file"
                      id="pharma-csv"
                      accept=".csv,.txt"
                      onChange={(e) => handleAttendeeCSV(e, "pharma_presence")}
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => document.getElementById("pharma-csv").click()}
                    >
                      <Upload className="w-3 h-3 mr-1" />
                      Import Pharma
                    </Button>
                    <input
                      type="file"
                      id="investor-csv"
                      accept=".csv,.txt"
                      onChange={(e) => handleAttendeeCSV(e, "investor_presence")}
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => document.getElementById("investor-csv").click()}
                    >
                      <Upload className="w-3 h-3 mr-1" />
                      Import Investors
                    </Button>
                  </div>
                </div>
              </div>

              <div>
                <Label>Pharma & Biotech Presence</Label>
                <p className="text-xs text-slate-500 mb-2">Companies expected to attend</p>
                <div className="flex flex-wrap gap-2 mb-2">
                  {eventForm.pharma_presence.map((company, i) => (
                    <Badge key={i} variant="secondary" className="gap-1">
                      {company}
                      <X className="w-3 h-3 cursor-pointer" onClick={() => removeTag("pharma_presence", company)} />
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="Add company (e.g., AstraZeneca) and press Enter"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addTag("pharma_presence", e.target.value);
                        e.target.value = "";
                      }
                    }}
                  />
                </div>
              </div>

              <div>
                <Label>Investors Expected</Label>
                <p className="text-xs text-slate-500 mb-2">VCs and investors expected to attend</p>
                <div className="flex flex-wrap gap-2 mb-2">
                  {eventForm.investor_presence.map((investor, i) => (
                    <Badge key={i} className="bg-teal-100 text-teal-700 gap-1">
                      {investor}
                      <X className="w-3 h-3 cursor-pointer" onClick={() => removeTag("investor_presence", investor)} />
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="Add investor (e.g., Syncona) and press Enter"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addTag("investor_presence", e.target.value);
                        e.target.value = "";
                      }
                    }}
                  />
                </div>
              </div>

              <div>
                <Label>Estimated Attendees</Label>
                <Input
                  value={eventForm.estimated_attendees}
                  onChange={(e) => setEventForm({ ...eventForm, estimated_attendees: e.target.value })}
                  placeholder="e.g., 3,500+"
                />
              </div>

              <div>
                <Label>Networking Score (0-100)</Label>
                <p className="text-xs text-slate-500 mb-2">How good is this event for networking?</p>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={eventForm.networking_score}
                  onChange={(e) => setEventForm({ ...eventForm, networking_score: parseInt(e.target.value) || 0 })}
                />
              </div>
            </TabsContent>

            <TabsContent value="speakers" className="space-y-4">
              {/* CSV Upload for Speakers */}
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium">Bulk Import Speakers</Label>
                    <p className="text-xs text-slate-500">Upload CSV with columns: Name, Title, Company</p>
                  </div>
                  <div>
                    <input
                      type="file"
                      id="speakers-csv"
                      accept=".csv"
                      onChange={handleSpeakerCSV}
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => document.getElementById("speakers-csv").click()}
                    >
                      <Upload className="w-3 h-3 mr-1" />
                      Import CSV
                    </Button>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between mb-4">
                <div>
                  <Label>Key Speakers</Label>
                  <p className="text-xs text-slate-500">Notable speakers at this event</p>
                </div>
                <Button variant="outline" size="sm" onClick={addSpeaker}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Speaker
                </Button>
              </div>

              {eventForm.key_speakers.length === 0 ? (
                <div className="text-center py-8 text-slate-500 border-2 border-dashed rounded-lg">
                  <Users className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                  <p>No speakers added yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {eventForm.key_speakers.map((speaker, index) => (
                    <div key={index} className="p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-start gap-3">
                        <div className="flex-1 grid grid-cols-3 gap-2">
                          <Input
                            placeholder="Name"
                            value={speaker.name}
                            onChange={(e) => updateSpeaker(index, "name", e.target.value)}
                          />
                          <Input
                            placeholder="Title"
                            value={speaker.title}
                            onChange={(e) => updateSpeaker(index, "title", e.target.value)}
                          />
                          <Input
                            placeholder="Company"
                            value={speaker.company}
                            onChange={(e) => updateSpeaker(index, "company", e.target.value)}
                          />
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeSpeaker(index)}
                          className="text-slate-400 hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="details" className="space-y-4">
              <div>
                <Label>BD Opportunities</Label>
                <p className="text-xs text-slate-500 mb-2">Describe business development opportunities at this event</p>
                <Textarea
                  value={eventForm.bd_opportunities}
                  onChange={(e) => setEventForm({ ...eventForm, bd_opportunities: e.target.value })}
                  placeholder="Ideal for Series A+ companies seeking pharma partnerships..."
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Registration Fee</Label>
                  <Input
                    value={eventForm.registration_fee}
                    onChange={(e) => setEventForm({ ...eventForm, registration_fee: e.target.value })}
                    placeholder="e.g., €1,800 - €2,500"
                  />
                </div>
                <div>
                  <Label>Website</Label>
                  <Input
                    value={eventForm.website}
                    onChange={(e) => setEventForm({ ...eventForm, website: e.target.value })}
                    placeholder="https://www.example.com"
                  />
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setEventDialog({ open: false, event: null })}>
              Cancel
            </Button>
            <Button onClick={saveEvent} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {eventDialog.event ? "Save Changes" : "Create Event"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, event: deleteDialog.event })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Event</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{deleteDialog.event?.name}</strong>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, event: null })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={deleteEvent} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete Event
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Dialog */}
      <Dialog open={uploadDialog.open} onOpenChange={(open) => setUploadDialog({ open })}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              Bulk Upload Events
            </DialogTitle>
            <DialogDescription>
              Upload a CSV file with event data. Required columns: name, start_date
            </DialogDescription>
          </DialogHeader>

          {uploadResult ? (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <h4 className="font-semibold text-green-900 flex items-center gap-2">
                  <Check className="w-5 h-5" />
                  Upload Complete
                </h4>
                <div className="mt-2 text-sm text-green-800">
                  <p>Created: {uploadResult.created} events</p>
                  <p>Updated: {uploadResult.updated} events</p>
                  <p>Total processed: {uploadResult.total_processed}</p>
                </div>
              </div>
              {uploadResult.errors?.length > 0 && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <h4 className="font-semibold text-amber-900">Warnings</h4>
                  <ul className="mt-2 text-sm text-amber-800 list-disc list-inside">
                    {uploadResult.errors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}
              <DialogFooter>
                <Button onClick={() => { setUploadDialog({ open: false }); setUploadResult(null); }}>
                  Done
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <>
              <div className="p-4 bg-slate-50 rounded-lg">
                <h4 className="font-medium text-slate-900 mb-2">Preview ({uploadData.length} events)</h4>
                <div className="max-h-60 overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Start Date</TableHead>
                        <TableHead>Location</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {uploadData.slice(0, 10).map((event, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-medium">{event.name}</TableCell>
                          <TableCell>{event.start_date}</TableCell>
                          <TableCell>{event.location || event.city || "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {uploadData.length > 10 && (
                    <p className="text-sm text-slate-500 mt-2 text-center">
                      ... and {uploadData.length - 10} more
                    </p>
                  )}
                </div>
              </div>

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                <h4 className="font-medium text-blue-900 mb-2">CSV Format Guide</h4>
                <p className="text-blue-800">
                  <strong>Required:</strong> name, start_date<br />
                  <strong>Optional:</strong> description, end_date, location, city, country, event_type, 
                  themes (comma-separated), target_audience (comma-separated), 
                  pharma_presence (comma-separated), investor_presence (comma-separated),
                  networking_score, bd_opportunities, estimated_attendees, registration_fee, website<br />
                  <strong>Speakers format:</strong> Name|Title|Company;Name2|Title2|Company2
                </p>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setUploadDialog({ open: false })}>
                  Cancel
                </Button>
                <Button onClick={handleBulkUpload} disabled={uploading} className="bg-teal-600 hover:bg-teal-700">
                  {uploading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Upload {uploadData.length} Events
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
